import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LenderAverageRatePointViewModel } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { AVERAGE_RATE_TIERS, LOOK_TO_BOOK_CHART_HEIGHT } from '../../../../constants';
import { formatPercentValue, mapAverageRateByLenderDataset } from '../../../../lib';

interface Props {
  rows: LenderAverageRatePointViewModel[];
  loading?: boolean;
}

const tierColors = AVERAGE_RATE_TIERS.map(tier => tier.color);
// Piecewise thresholds use exclusive upper bounds; add a small epsilon to the last threshold
// so that exactly 10% falls in Mid (amber), not High (red), matching the "8–10%" label.
const tierThresholds = AVERAGE_RATE_TIERS.slice(0, -1).map((tier, i, arr) =>
  i === arr.length - 1 ? tier.max + 0.001 : tier.max
);

const LABEL_CHAR_WIDTH = 7;

export const AverageRateByLenderChart = ({ rows, loading }: Props) => {
  const dataset = mapAverageRateByLenderDataset(rows);

  const maxLenderNameLength = dataset.reduce((max, row) => Math.max(max, row.lender.length), 0);
  const leftMargin = maxLenderNameLength * LABEL_CHAR_WIDTH;

  return (
    <CardContainer title="Average Rate by Lender">
      {loading ? (
        <Skeleton variant="rounded" height={LOOK_TO_BOOK_CHART_HEIGHT} />
      ) : (
        <>
          <Stack direction="row" gap={3} sx={{ mb: 1 }}>
            {AVERAGE_RATE_TIERS.map(tier => (
              <Stack key={tier.label} direction="row" alignItems="center" gap={0.5}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: tier.color }} />
                <Typography variant="body2" color="text.secondary">
                  {tier.label}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <BarChart
            height={LOOK_TO_BOOK_CHART_HEIGHT}
            layout="horizontal"
            dataset={dataset}
            margin={{ left: leftMargin }}
            barLabel={item => {
              if (!item.value) return null;
              const row = dataset[item.dataIndex];
              return row ? formatPercentValue(row.avgRate) : null;
            }}
            yAxis={[{ scaleType: 'band', dataKey: 'lender' }]}
            xAxis={[
              {
                min: 0,
                tickMinStep: 2,
                valueFormatter: value => `${value}%`,
                colorMap: { type: 'piecewise', thresholds: tierThresholds, colors: tierColors },
              },
            ]}
            series={[
              { dataKey: 'avgRate', label: 'Avg rate', valueFormatter: value => formatPercentValue(value as number) },
            ]}
            slotProps={{ legend: { hidden: true } }}
          />
        </>
      )}
    </CardContainer>
  );
};
